import { deleteUserAvatar, uploadUserAvatar, userState } from '@hefe/store';
import { formatDate, fullUserName, userHasRole } from '@hefe/utils';
import {
  Button,
  Col,
  Descriptions,
  message,
  Popover,
  Row,
  Space,
  Spin,
  Typography,
  Upload,
} from 'antd';
import dayjs from 'dayjs';
import { t } from '@hefe/i18n';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import UserAvatar from '../../../core/components/user/user-avatar/user-avatar/user-avatar.component';
import { RcFile } from 'antd/lib/upload';
import { useAppDispatch } from '../../../../store/store';
import { AuthRoles } from '@hefe/auth';

export default function UserProfilePage() {
  const [uploadingImage, setUploadingImage] = useState(false);
  const dispatch = useAppDispatch();
  const stateUser = useSelector(userState);
  const user = stateUser.user;

  if (!user) {
    return null;
  }

  const olderThan18 = dayjs().diff(dayjs(user.birthday), 'years') >= 18;

  const beforeUpload = (file: RcFile) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error(t('errors.avatar_upload.wrong_format'));
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(t('errors.avatar_upload.too_large'));
    }

    if (isJpgOrPng && isLt2M) {
      uploadImage(file);
    }

    return false;
  };

  const uploadImage = (file: RcFile) => {
    setUploadingImage(true);

    dispatch(
      uploadUserAvatar({
        file: file,
        base64: false,
      })
    )
      .unwrap()
      .finally(() => setUploadingImage(false));
  };

  const deleteImage = () => {
    setUploadingImage(true);

    dispatch(deleteUserAvatar())
      .unwrap()
      .finally(() => setUploadingImage(false));
  };

  const renderUserAvatar = () => {
    return (
      <Space direction="vertical" align="center">
        <Popover
          content={
            <Space direction="vertical">
              <Space>
                <Upload
                  accept=".jpg, .jpeg, .png"
                  name="user_icon"
                  className="avatar-uploader"
                  showUploadList={false}
                  beforeUpload={beforeUpload}
                  disabled={uploadingImage}
                  multiple={false}
                >
                  <Button loading={uploadingImage} icon={<EditOutlined />}>
                    {t('edit')}
                  </Button>
                </Upload>
                {user.avatar && (
                  <Button
                    onClick={() => deleteImage()}
                    icon={<DeleteOutlined />}
                  >
                    {t('delete')}
                  </Button>
                )}
              </Space>
              <Typography.Text>max. 2MB - .jpg / .png</Typography.Text>
            </Space>
          }
        >
          <div style={{ cursor: 'pointer' }}>
            {uploadingImage ? (
              <Spin size="large" />
            ) : (
              <UserAvatar size={150} user={user} />
            )}
          </div>
        </Popover>
        <Typography.Title level={3}>
          {user.firstname} {user.lastname}
        </Typography.Title>
      </Space>
    );
  };

  return (
    <div>
      <Row justify="center" style={{ marginBottom: 24 }}>
        <Col>{renderUserAvatar()}</Col>
      </Row>
      <Row justify="center">
        <Col xs={24} md={12}>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('name')}>
              {user.firstname} {user.lastname}
            </Descriptions.Item>
            <Descriptions.Item label={t('birthday')}>
              {formatDate(user.birthday ?? '')}
            </Descriptions.Item>
            <Descriptions.Item label={t('email')}>
              {user.email}
            </Descriptions.Item>
            <Descriptions.Item label={t('address')}>
              <Space direction="vertical" size={0}>
                <Typography.Text>{user.street}</Typography.Text>
                <Typography.Text>
                  {user.zipcode} {user.city}
                </Typography.Text>
                <Typography.Text>{user.country}</Typography.Text>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t('contact')}>
              <Space direction="vertical" size={0}>
                <Typography.Text>
                  {t('phone')}: {user.phone}
                </Typography.Text>
                <Typography.Text>
                  {t('represented_by')}:{' '}
                  {olderThan18 ? t('full_age') : user.represented_by}
                </Typography.Text>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t('roles.roles')}>
              <ul>
                {user.role.map((role) => {
                  return <li key={role}>{t(`roles.${role}`)}</li>;
                })}
              </ul>
            </Descriptions.Item>
            <Descriptions.Item label={`${t('teams')} (${t('player')})`}>
              <ul>
                {user?.basketball_user?.player_teams?.map((team) => {
                  return <li key={team.id}>{team.name}</li>;
                })}
              </ul>
            </Descriptions.Item>
            {userHasRole(AuthRoles.bbcoach, user) && (
              <Descriptions.Item label={`${t('teams')} (${t('coach')})`}>
                <ul>
                  {user?.basketball_user?.coached_teams_ignore_admin?.map(
                    (team) => {
                      return <li key={team.id}>{team.name}</li>;
                    }
                  )}
                </ul>
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t('linked_accounts')}>
              <ul>
                {user.child_users.map((user) => {
                  return <li key={user.id}>{fullUserName(user)}</li>;
                })}
              </ul>
            </Descriptions.Item>
          </Descriptions>
        </Col>
      </Row>
    </div>
  );
}
